import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { Badge } from 'src/@/components/ui/badge';
import useAuth from 'src/hooks/useAuth';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import { laisMcpUrl } from 'src/config';
import axios from '../../../../../utils/axios';
import GenericTable from '../../../../../components/table/GenericTable';
import { GENERIC_TABLE_DESIGN } from '../../../../../components/table/GenericTable.design';
import { APIS_COMPONENT_DESIGN } from '../ApisComponent.design';
import { MCP_TOKENS_DESIGN } from './McpTokens.design';
import ModalCriarMcpToken from './ModalCriarMcpToken';

// `moment().fromNow()` sai em ingles nesta app (locale pt-BR nunca e carregado
// globalmente) — mesmo ajuste local ja usado em painel-ia/ConversationSidebar.js.
function quando(data) {
    if (!data) return null;
    const d = moment(data);
    if (!d.isValid()) return null;
    const minutos = moment().diff(d, 'minutes');
    if (minutos < 1) return 'agora';
    if (minutos < 60) return `há ${minutos} min`;
    const horas = moment().diff(d, 'hours');
    if (horas < 24) return `há ${horas} h`;
    const dias = moment().diff(d, 'days');
    if (dias === 1) return 'ontem';
    if (dias < 30) return `há ${dias} dias`;
    return d.format('DD/MM/YYYY');
}

function maskToken(preview) {
    return `sk_mcp_${'•'.repeat(8)}${preview || ''}`;
}

export default function MainMcpTokens() {
    const { t } = useTranslation('settings');
    const { user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [openConfirmacao, setOpenConfirmacao] = useState(false);
    const [selectedId, setSelectedId] = useState('');

    const listar = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get('/v3/mcp-tokens');
            const payload = response?.data;
            const list = Array.isArray(payload?.data) ? payload.data : [];
            setTokens(list);
        } catch (error) {
            console.error('Erro ao listar tokens MCP:', error);
            setTokens([]);
            enqueueSnackbar(t('apiKeys.mcp.errors.listFailed', 'Erro ao listar tokens MCP'), { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, t]);

    useEffect(() => {
        listar();
    }, [listar]);

    // Chamado pelo modal de criacao — devolve { token, data } pro modal exibir
    // o valor pleno uma unica vez, e ja insere a linha (sem token) na tabela.
    const handleCriar = async (label) => {
        try {
            const response = await axios.post('/v3/mcp-tokens', { label });
            const { token, data } = response.data || {};
            setTokens((prev) => [data, ...(Array.isArray(prev) ? prev : [])]);
            enqueueSnackbar(t('apiKeys.mcp.messages.createSuccess', 'Token criado com sucesso!'), { variant: 'success' });
            return { token, data };
        } catch (error) {
            console.error('Erro ao criar token MCP:', error);
            throw error?.response?.data || error;
        }
    };

    const handleRevogar = async () => {
        try {
            await axios.delete(`/v3/mcp-tokens/${selectedId}`);
            setTokens((prev) => prev.filter((tk) => tk._id !== selectedId));
            setOpenConfirmacao(false);
            setSelectedId('');
            enqueueSnackbar(t('apiKeys.mcp.messages.deleteSuccess', 'Token revogado com sucesso!'), { variant: 'success' });
        } catch (error) {
            console.error('Erro ao revogar token MCP:', error);
            enqueueSnackbar(error?.mensagem || t('apiKeys.mcp.errors.deleteFailed', 'Erro ao revogar token MCP'), { variant: 'error' });
        }
    };

    const TABLE_HEAD = [
        { id: 'nome', label: t('apiKeys.mcp.table.nameColumn', 'Nome'), className: 'flex-1' },
        { id: 'token', label: t('apiKeys.mcp.table.tokenColumn', 'Token'), className: 'w-64' },
        { id: 'ultimoUso', label: t('apiKeys.mcp.table.lastUsedColumn', 'Último uso'), className: 'w-32' },
        { id: 'status', label: t('apiKeys.mcp.table.statusColumn', 'Status'), className: 'w-28' },
        { id: 'actions', label: t('buttons.actions', { ns: 'common' }), className: 'w-24 text-right' },
    ];

    return (
        <div>
            <ModalConfimacao
                open={openConfirmacao}
                onClose={() => setOpenConfirmacao(false)}
                onConfirm={handleRevogar}
                mensagem={t('apiKeys.mcp.deleteConfirmMessage', 'Deseja mesmo revogar este token? Qualquer Claude Code conectado com ele perde acesso imediatamente.')}
            />

            <ModalCriarMcpToken
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSubmit={handleCriar}
                laisMcpUrl={laisMcpUrl}
                clienteId={user?.clienteId}
            />

            <div className="flex justify-end mb-4">
                <button onClick={() => setOpenModal(true)} className={APIS_COMPONENT_DESIGN.page.actionButton}>
                    <Plus size={18} />
                    <span>{t('apiKeys.mcp.newTokenButton', 'Novo token')}</span>
                </button>
            </div>

            {(tokens?.length ?? 0) === 0 && !loading ? (
                <div className={APIS_COMPONENT_DESIGN.emptyState.container}>
                    <div className={APIS_COMPONENT_DESIGN.emptyState.iconBox}>
                        <KeyRound size={32} />
                    </div>
                    <h3 className={APIS_COMPONENT_DESIGN.emptyState.title}>{t('apiKeys.mcp.emptyState.title', 'Nenhum token criado')}</h3>
                    <p className={APIS_COMPONENT_DESIGN.emptyState.description}>
                        {t('apiKeys.mcp.emptyState.description', 'Crie um token para conectar seu Claude Code aos dados desta conta.')}
                    </p>
                    <button onClick={() => setOpenModal(true)} className={APIS_COMPONENT_DESIGN.emptyState.button}>
                        {t('apiKeys.mcp.emptyState.button', 'Criar primeiro token')}
                    </button>
                </div>
            ) : (
                <GenericTable headLabels={TABLE_HEAD}>
                    {(tokens || []).map((row) => (
                        <div key={row._id} className={GENERIC_TABLE_DESIGN.row.container}>
                            <div className={GENERIC_TABLE_DESIGN.common.colMain}>
                                <div className="flex items-center gap-3">
                                    <div className={GENERIC_TABLE_DESIGN.common.iconBox}>
                                        <KeyRound size={20} />
                                    </div>
                                    <h3 className="text-sm font-semibold text-gray-900">{row.label}</h3>
                                </div>
                            </div>

                            <div className="w-64">
                                <code className={MCP_TOKENS_DESIGN.tokenChip}>{maskToken(row.tokenPreview)}</code>
                            </div>

                            <div className="w-32 text-sm text-gray-500">
                                {quando(row.ultimoUsoEm) || t('apiKeys.mcp.table.neverUsed', 'Nunca usado')}
                            </div>

                            <div className="w-28">
                                {row.status === 'revogado' ? (
                                    <Badge variant="outline" className={MCP_TOKENS_DESIGN.status.revogado}>
                                        <span className={MCP_TOKENS_DESIGN.status.revogadoDot} />
                                        {t('apiKeys.mcp.status.revogado', 'Revogado')}
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className={MCP_TOKENS_DESIGN.status.ativo}>
                                        <span className={MCP_TOKENS_DESIGN.status.ativoDot} />
                                        {t('apiKeys.mcp.status.ativo', 'Ativo')}
                                    </Badge>
                                )}
                            </div>

                            <div className="w-24 flex justify-end gap-1">
                                <Tooltip title={t('apiKeys.mcp.table.revokeTooltip', 'Revogar')}>
                                    <button
                                        onClick={() => {
                                            setSelectedId(row._id);
                                            setOpenConfirmacao(true);
                                        }}
                                        className={GENERIC_TABLE_DESIGN.common.deleteButton}
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </Tooltip>
                            </div>
                        </div>
                    ))}
                </GenericTable>
            )}
        </div>
    );
}
